import { getConfig } from '~/lib/config'
import { algoliasearch } from 'algoliasearch'
import type { SearchClient } from 'algoliasearch'
import { RecordType } from '~/types/records/RecordType'
import { decodeUrlParamValue, encodeUrlParamValue } from '~/lib/url'

/**
 * Initialize an Algolia search client
 */
export const getSearchClient = (): SearchClient => {
  const { ALGOLIA } = getConfig()

  return algoliasearch(
    ALGOLIA.APP_ID,
    ALGOLIA.SEARCH_KEY,
  )
}
/**
 * Get the Algolia index name for a record type
 * @param recordType
 */
export const recordTypeToIndexName = (recordType: RecordType): string => {
  const { ALGOLIA } = getConfig()

  return `${ALGOLIA.INDEX_PREFIX}${recordTypeToIndexSlug(recordType)}${ALGOLIA.INDEX_SUFFIX}`
}
/**
 * Get the index slug for a record type
 * @param recordType
 */
export const recordTypeToIndexSlug = (recordType: RecordType): string =>  `${recordType.toLowerCase()}s`

/**
 * Convert a Laravel UUID to an Algolia object ID
 * @param uuid
 * @param recordType
 */
export const uuidToObjectId = (uuid: string, recordType: RecordType) => 'App\\Models\\' + recordType + '::' + uuid
/**
 * Get Algolia objects by UUID
 * @param recordType
 * @param uuids
 */
export const getObjects = async (recordType: RecordType, uuids: string[]): Promise<unknown> => {
  const client = getSearchClient()
  const indexName = recordTypeToIndexName(recordType)
  const objectIds = uuids.map(uuid => uuidToObjectId(uuid, recordType))
  const objects = await client.getObjects({
    requests: objectIds.map(objectID => ({ indexName, objectID })),
  })

  return objects.results
}

export interface RouteState {
  search?: string
  page?: number
  [key: string]: string | number | undefined
}

export interface UiState {
  [key: string]: {
    query?: string
    page?: number
    toggle?: {
      [key: string]: boolean
    }
    refinementList?: {
      [key: string]: string[]
    }
    menu?: {
      [key: string]: string
    }
  }
}

export const stateToRoute = (indexName: string, uiState: UiState) => {
  const {
    query,
    page,
    menu = {},
  } = uiState[indexName]

  return {
    search: query,
    page,
    ...Object.entries(menu as object).reduce((acc, [key, val]) => {
      acc[key] = encodeUrlParamValue(val.toString())
      return acc
    }, {}),
  }
}

export const routeToState = (indexName: string, routeState: RouteState) => {
  const {
    search: query,
    page,
    ...menu
  } = routeState

  return {
    [indexName]: {
      query,
      page,
      menu: Object.entries(menu).reduce((acc, [key, val]) => {
        if (val) {
          acc[key] = decodeUrlParamValue(val.toString())
        }
        return acc
      }, {}),
    },
  }
}
